import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import axios, { type AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import {
  type SecondarySearchHitCassettes,
  type ShopWorkCassetteProps,
} from "@userFrontend/features/publication/types/Cassettes";
import {
  omitEmploymentName,
  formatSalaryPriceRound,
  chooseSalaryUnit,
} from "@root/userFrontend/utils/formatSalary";
import {
  type SecondarySearchHitCassettesProps,
  type SecondarySearchHitCassettesResponseProps,
} from "@userFrontend/features/publication/types/SecondarySearchHitCassettes";

type Props = SecondarySearchHitCassettesProps & {
  scoutedFlag: boolean;
};

export const useSecondarySearchHitCassettes = ({
  url,
  params,
  query,
  workCode,
  totalNumber,
  scoutedFlag,
}: Props) => {
  const [shops, setShops] = useState<ShopWorkCassetteProps[]>([]);
  const [page, setPage] = useState(1);
  const [isShowIndicator, setIsShowIndicator] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // 受け取ったデータを、使用するコンポーネントのpropsに合わせて整形する
  const formatData = useCallback(
    (fetchedShops: SecondarySearchHitCassettes) => {
      const formattedData = fetchedShops.map(
        ({ docId, detailUrl, imgSrc, title, shopName, shopAddress, salaries, actionContent }) => {
          const employmentAndSalaryType = salaries.map((salary) => ({
            employmentName: omitEmploymentName(salary.employmentTypeName),
            salaryMax: formatSalaryPriceRound(salary.maxPrice),
            salaryMinUnit: chooseSalaryUnit(salary.maxPrice),
            salaryMin: formatSalaryPriceRound(salary.minPrice),
            salaryMaxUnit: chooseSalaryUnit(salary.minPrice),
            salaryTypeName: salary.salaryTypeName,
          }));

          return {
            docId,
            url: detailUrl,
            imgSrc,
            title,
            shopName,
            shopAddress: {
              prefecture: shopAddress.prefecture,
              city: shopAddress.city,
              station: shopAddress.station,
            },
            employmentAndSalaryType,
            enterableFlag: actionContent.entry.enterable,
            entriedFlag: actionContent.entry.hasEntered,
            scoutedFlag:
              scoutedFlag !== undefined ? scoutedFlag : actionContent.scoutApprove.scoutedFlag,
            workCode: workCode || "",
            scoutedEntryUrl: actionContent.scoutApprove.scoutedEntryUrl,
            query: query || "",
            entryUrl: actionContent.entry.url,
          };
        },
      );
      return formattedData;
    },
    [query, scoutedFlag, workCode],
  );

  const fetchWorks = useCallback(async () => {
    await axios
      .get(url, { params: { ...params, page } })
      .then(({ data }: SecondarySearchHitCassettesResponseProps) => {
        // eslint-disable-next-line camelcase
        const { cassettes, total_count } = data;

        // 重複する店舗の求人を除外してnewShopWorksに格納する
        const fetchedAndFormattedOtherWorks = formatData(cassettes);
        const detailUrls = shops.map((shop) => shop.url);
        const newShopWorks = fetchedAndFormattedOtherWorks.filter(
          (shopWork) => !detailUrls.includes(shopWork.url),
        );

        // 万が一取得できる店舗の求人がなくなってもインジケーターが残っていた場合は、インジケーターを非表示にして処理を抜ける
        if (!newShopWorks.length) {
          setIsShowIndicator(false);
          return;
        }

        if (!totalCount) {
          setTotalCount(total_count);
        }

        // 既存の店舗の求人に追加分の店舗の求人を加える
        setShops((prev) => [...prev, ...newShopWorks]);
        setPage((prev) => prev + 1);
      })
      .catch((error: AxiosError) => {
        sendErrorLog(new Error(error.message || "店舗カセットのデータを取得できませんでした"));
      });
  }, [formatData, page, params, shops, totalCount, url]);

  // 全ての店舗ごと求人を表示したらインジケーターを非表示にする
  useEffect(() => {
    if (!totalNumber) return;
    if (shops.length >= totalNumber) {
      setIsShowIndicator(false);
    }
  }, [shops.length, totalNumber]);

  return { shops, fetchWorks, isShowIndicator, totalCount };
};
